import { createContext, useState, useContext, useEffect } from "react";
import AuthContext from "./AuthContext";
import useCan from "hooks/useCan";
import { CAPABILITIES } from "utils/capabilities";
import logger from "utils/logger";
import {
  getHostRecruitmentEmailTemplate,
  updateHostRecruitmentEmailTemplate,
  createHostRecruitmentEmailTemplate,
  getHostRecruitmentTemplateVariables,
  getNonHostCustomers
} from "Services/storeService";

/**
 * Context for Host Recruitment feature
 * Manages email template data, variables, and potential host customers
 */
const HostRecruitmentContext = createContext();

export default HostRecruitmentContext;

/**
 * Provider component for Host Recruitment context
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Context provider
 */
export const HostRecruitmentProvider = ({ children }) => {
  const { userProfile } = useContext(AuthContext);
  // Host recruitment lives on the Real Customers page. Only roles that can see
  // it get the store id, so the fetches below never fire for anyone else.
  const { allowed: canViewHosts } = useCan(CAPABILITIES.HOSTS_VIEW);
  const storeId = canViewHosts ? userProfile?.store?.id : undefined;

  const [templateId, setTemplateId] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [hosts, setHosts] = useState([]);
  const [hostsLoading, setHostsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Form state (global to persist across tab navigation)
  const [formData, setFormData] = useState({
    subject: "",
    body: "",
    bodyHtml: "",
    footer: "",
    signupUrl: "",
    ctaText: ""
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);



  /**
 * Fetches the email template from the API
 * @throws {Error} If storeId is not found or API call fails
 * @returns {Promise<Object|null>} Template data or null
 */
const fetchTemplate = async () => {
    if (!storeId) {
      setLoading(false);
      throw new Error("Store ID not found");
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getHostRecruitmentEmailTemplate(storeId);
      if (response?.data) {
        const data = response.data;
        setTemplateId(data.id);
        setTemplateData(data);
        return data;
      }
      return null;
    } catch (err) {
      logger.error("Error fetching template:", err);
      setError(err.response?.data?.message || "Failed to fetch template");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
 * Fetches template variables from the API
 * @throws {Error} If storeId is not found or API call fails
 * @returns {Promise<Array>} Array of variable objects with label and value
 */
const fetchVariables = async () => {
    if (!storeId) {
      throw new Error("Store ID not found");
    }

    try {
      const response = await getHostRecruitmentTemplateVariables(storeId);
      if (response?.data && Array.isArray(response.data)) {
        const vars = response.data.map(v => ({
          label: v.label,
          value: v.value
        }));
        setVariables(vars);
        return vars;
      }
      setVariables([]);
      return [];
    } catch (err) {
      logger.error("Error fetching variables:", err);
      throw err;
    }
  };

  /**
 * Saves or updates the email template
 * @param {Object} data - Template data to save
 * @param {string} data.subject - Email subject
 * @param {string} data.title - Email title
 * @param {string} data.buttonText - CTA button text
 * @param {string} data.body - Plain text body
 * @param {string} data.bodyHtml - HTML body content
 * @param {string} data.footer - Email footer
 * @param {File|null} logoFile - Logo file to upload (required for new templates)
 * @throws {Error} If storeId is not found, logo missing for new template, or API fails
 * @returns {Promise<Object>} Saved template data
 */
const saveTemplate = async (data, logoFile = null) => {
    if (!storeId) {
      throw new Error("Store ID not found");
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      if (templateId) {
        if (logoFile) {
          const formData = new FormData();
          formData.append('email_template_logo', logoFile);
          formData.append('email_template_subject', data.subject);
          formData.append('email_template_title', data.title);
          formData.append('email_template_button_text', data.buttonText);
          formData.append('email_template_body', data.body);
          formData.append('email_template_body_html', data.bodyHtml);
          formData.append('email_template_footer', data.footer);
          formData.append('email_template_is_active', 'true');
          response = await updateHostRecruitmentEmailTemplate(storeId, formData, true);
        } else {
          const payload = {
            email_template_subject: data.subject,
            email_template_title: data.title,
            email_template_button_text: data.buttonText,
            email_template_body: data.body,
            email_template_body_html: data.bodyHtml,
            email_template_footer: data.footer,
            email_template_is_active: true
          };
          response = await updateHostRecruitmentEmailTemplate(storeId, payload);
        }
      } else {
        if (!logoFile) {
          throw new Error("Logo is required for new templates");
        }

        const formData = new FormData();
        formData.append('email_template_logo', logoFile);
        formData.append('email_template_subject', data.subject);
        formData.append('email_template_title', data.title);
        formData.append('email_template_button_text', data.buttonText);
        formData.append('email_template_body', data.body);
        formData.append('email_template_body_html', data.bodyHtml);
        formData.append('email_template_footer', data.footer);
        formData.append('email_template_is_active', 'true');

        response = await createHostRecruitmentEmailTemplate(storeId, formData);
        
        if (response?.data) {
          setTemplateId(response.data.id);
        }
      }

     
      const savedTemplateData = {
        ...(templateData || {}),
        ...(response?.data || {}),

        email_template_subject:
          response?.data?.email_template_subject ?? data.subject,

        email_template_title:
          response?.data?.email_template_title ?? data.title,

        email_template_button_text:
          response?.data?.email_template_button_text ?? data.buttonText,

        email_template_body:
          response?.data?.email_template_body ?? data.body,

        email_template_body_html:
          response?.data?.email_template_body_html ?? data.bodyHtml,

        email_template_footer:
          response?.data?.email_template_footer ?? data.footer,

        email_template_is_active: true,
        updated_at: new Date().toISOString()
      };

      
      setTemplateData(savedTemplateData);

      setLoading(false);
      return response?.data;
    } catch (err) {
      logger.error("Error saving template:", err);
      setError(err.response?.data?.message || "Failed to save template");
      setLoading(false);
      throw err;
    }
  };

  /**
   * Fetch non-host customers (potential hosts)
   * @param {number} page - Page number
   * @param {string} search - Search query
   * @param {boolean} append - Whether to append to existing hosts
   * @param {Object} filters - Additional filters (min_spent, max_spent, etc.)
   * @returns {Promise<Object>} - Response data
   */
  const fetchNonHosts = async (page = 1, search = '', append = false, filters = {}) => {
    if (!storeId) return;
    
    setHostsLoading(true);
    try {
      const params = {
        page,
        page_size: 20,
        ...(search && { search }),
        ...filters
      };
      
      const response = await getNonHostCustomers(storeId, params);
      const data = response.data;
      
  const mappedHosts = (data.results || []).map(customer => ({
        id: customer.id,
        name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || customer.display_name || 'N/A',
        email: customer.email || 'N/A',
        phone: customer.phone || 'N/A',
        amount: customer.total_amount_spent ? `$${parseFloat(customer.total_amount_spent).toFixed(2)}` : 'N/A',
        invitationHistory: customer.invitation_history || []
      }));
      
      if (append) {
        setHosts(prev => [...prev, ...mappedHosts]);
      } else {
        setHosts(mappedHosts);
      }
      
      setCurrentPage(page);
      setTotalCount(data.count || 0);
      setHasMore(!!data.next);
      
      return data;
    } catch (error) {
      logger.error('Error fetching non-hosts:', error);
      throw error;
    } finally {
      setHostsLoading(false);
    }
  };

  const [dataInitialized, setDataInitialized] = useState(false);
  
  /**
   * Initializes host recruitment data on mount or when storeId changes
   * Fetches template and variables in parallel
   * Only runs once per storeId to avoid unnecessary API calls
   */
  useEffect(() => {
    const initializeData = async () => {
      if (dataInitialized && templateData) {
        return;
      }
      
      if (storeId) {
        setLoading(true);
        try {
          await Promise.all([
            fetchTemplate(),
            fetchVariables()
          ]);
          setDataInitialized(true);
        } catch (err) {
          logger.error("Error initializing data:", err);
        } finally {
          setLoading(false);
        }
      }
    };

    initializeData();
  }, [storeId]);

  const contextData = {
    templateId,
    templateData,
    variables,
    loading,
    error,
    fetchTemplate,
    fetchVariables,
    saveTemplate,
    storeId,
    hosts,
    setHosts,
    hostsLoading,
    currentPage,
    totalCount,
    hasMore,
    fetchNonHosts,
    formData,
    setFormData,
    logoPreview,
    setLogoPreview,
    logoFile,
    setLogoFile
  };

  return (
    <HostRecruitmentContext.Provider value={contextData}>
      {children}
    </HostRecruitmentContext.Provider>
  );
};
